import { useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { useQueryClient } from "@tanstack/react-query";
import * as client from "../../client";
import { setFunctionInstructions } from "../../api/routes";
import { apiErrorDetail } from "../../api/errors";
import { useToast } from "../../context-providers/Toast";
import { jobKeys, reportKeys } from "./queryKeys";
import styles from "./FunctionInstructions.module.css";

interface FunctionInstructionsProps {
  reportId: string;
  /** The source file whose extracted functions the instructions steer. */
  filename: string;
  functionType: client.function_type;
  /** Instructions currently stored for this file, if any. */
  instructions: string | undefined;
}

/**
 * Row above a file's extracted limit/deductible functions showing the stored
 * extraction instructions for that file, with a dialog to change them. Saving
 * stores the text on the report and queues a function regen, so the corrected
 * functions land a few moments later.
 */
export const FunctionInstructions: React.FC<FunctionInstructionsProps> = ({
  reportId,
  filename,
  functionType,
  instructions,
}) => {
  const [open, setOpen] = useState(false);

  return (
    <div className={styles.row}>
      <i className={`pi pi-sparkles ${styles.icon}`} />
      <span className={styles.label}>Extraction instructions</span>
      {instructions ? (
        <span className={styles.text}>{instructions}</span>
      ) : (
        <span className={styles.empty}>None for this file</span>
      )}
      <Button
        label={instructions ? "Edit" : "Fix with instructions"}
        size="small"
        outlined
        onClick={() => setOpen(true)}
      />
      {open && (
        <FunctionInstructionsDialog
          reportId={reportId}
          filename={filename}
          functionType={functionType}
          instructions={instructions}
          onHide={() => setOpen(false)}
        />
      )}
    </div>
  );
};

const FunctionInstructionsDialog: React.FC<
  FunctionInstructionsProps & { onHide: () => void }
> = ({ reportId, filename, functionType, instructions, onHide }) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [value, setValue] = useState(instructions ?? "");
  const [saving, setSaving] = useState(false);
  const trimmed = value.trim();
  const readableType = functionType === "limit" ? "limit" : "deductible";

  const submit = async (next: string | null) => {
    setSaving(true);
    try {
      await setFunctionInstructions(reportId, filename, functionType, next);
      queryClient.invalidateQueries({ queryKey: reportKeys.detail(reportId) });
      queryClient.invalidateQueries({ queryKey: jobKeys.reportJobs(reportId) });
      toast.show({
        severity: "success",
        summary: next === null ? "Instructions cleared" : "Instructions saved",
        detail: `Regenerating the ${readableType} functions for ${filename}.`,
      });
      onHide();
    } catch (e) {
      toast.show({
        severity: "error",
        summary: "Failed to save instructions",
        detail: apiErrorDetail(e),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      header={`Fix ${readableType} functions: ${filename}`}
      visible={true}
      onHide={() => {
        if (saving) return;
        onHide();
      }}
      style={{ width: "520px" }}
      footer={
        <div className={styles.buttonContainer}>
          <Button
            label="Cancel"
            severity="secondary"
            text
            onClick={onHide}
            disabled={saving}
          />
          {instructions && (
            <Button
              label="Clear"
              severity="danger"
              outlined
              onClick={() => submit(null)}
              disabled={saving}
            />
          )}
          <Button
            label="Save & regenerate"
            onClick={() => submit(trimmed)}
            loading={saving}
            disabled={trimmed === "" || trimmed === instructions}
          />
        </div>
      }
    >
      <div className={styles.dialogBody}>
        <div>
          Tell the extraction model what it got wrong in this file. The text is
          stored with the report and added to every {readableType} extraction
          prompt for {filename}, including future reruns, then the functions are
          regenerated now.
        </div>
        <InputTextarea
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          rows={4}
          placeholder="e.g. The wind and hail deductible in this form is severe convective storm (scs), not named storm"
          className={styles.textarea}
          disabled={saving}
        />
      </div>
    </Dialog>
  );
};
